import { useRenderPhaseSync } from '@common/hooks';
import { Flex } from '@radix-ui/themes';
import { ChangeEvent, KeyboardEvent, ReactNode, useRef } from 'react';

export interface DigitInputProps {
    length: number;
    value: string;
    onChange: (value: string) => void;
    maxValues?: (number | undefined)[];
    renderSeparator?: (index: number) => ReactNode;
}

export const DigitInput = ({
    length,
    value,
    onChange,
    maxValues = [],
    renderSeparator,
}: DigitInputProps) => {
    const safeValue = value
        .replace(/\D/g, '')
        .padEnd(length, '0')
        .slice(0, length);

    const [localValue, setLocalValue] = useRenderPhaseSync(safeValue);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const updateDigit = (index: number, newDigit: string) => {
        const chars = localValue.split('');
        const updatedChars = chars.map((c, i) => (i === index ? newDigit : c));
        const newValue = updatedChars.join('');

        setLocalValue(newValue);
        onChange(newValue);
    };

    const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/\D/g, '').slice(-1);
        if (!raw) return;

        const max = maxValues[index];
        const formatted =
            max !== undefined && parseInt(raw, 10) > max ? max.toString() : raw;

        updateDigit(index, formatted);
        if (inputRefs.current[index + 1]) inputRefs.current[index + 1]?.focus();
    };

    const handleKeyDown = (
        index: number,
        e: KeyboardEvent<HTMLInputElement>,
    ) => {
        if (/^[0-9]$/.test(e.key)) {
            e.preventDefault();

            const max = maxValues[index];
            const formatted =
                max !== undefined && parseInt(e.key, 10) > max
                    ? max.toString()
                    : e.key;

            updateDigit(index, formatted);
            if (inputRefs.current[index + 1])
                inputRefs.current[index + 1]?.focus();
            return;
        }

        const actionMap: Record<string, () => void> = {
            Backspace: () => {
                e.preventDefault();
                updateDigit(index, '0');
                if (localValue[index] === '0' && inputRefs.current[index - 1]) {
                    inputRefs.current[index - 1]?.focus();
                }
            },
            ArrowLeft: () => {
                e.preventDefault();
                inputRefs.current[index - 1]?.focus();
            },
            ArrowRight: () => {
                e.preventDefault();
                inputRefs.current[index + 1]?.focus();
            },
        };

        actionMap[e.key]?.();
    };

    const inputStyle: React.CSSProperties = {
        width: '26px',
        height: '32px',
        padding: '0',
        textAlign: 'center',
        fontSize: 'var(--font-size-2)',
        color: 'var(--gray-12)',
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--gray-a7)',
        borderRadius: 'var(--radius-2)',
        outline: 'none',
        boxShadow: 'none',
        transition: 'border-color 0.15s, box-shadow 0.15s',
    };

    return (
        <Flex align='center' style={{ gap: '2px' }}>
            {Array.from({ length }).map((_, index) => {
                const sepNode = renderSeparator ? renderSeparator(index) : null;

                return (
                    <Flex align='center' style={{ gap: '2px' }} key={index}>
                        <input
                            ref={(el) => {
                                inputRefs.current[index] = el;
                            }}
                            value={localValue[index] || '0'}
                            style={inputStyle}
                            onChange={(e) => handleChange(index, e)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onFocus={(e) => {
                                e.target.style.borderColor = 'var(--accent-8)';
                                e.target.style.boxShadow =
                                    '0 0 0 1px var(--accent-8)';
                                e.target.select();
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = 'var(--gray-a7)';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                        {sepNode}
                    </Flex>
                );
            })}
        </Flex>
    );
};
